import { useCallback, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toolbar } from 'primereact/toolbar';
import { classNames } from 'primereact/utils';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import {
  GET_SEX,
  GET_TESTICULAR_DEVELOPMENT,
  GET_TIME_OF_DAY,
} from '@/types.gql';

import { initialCalfState } from './state';
import {
  BreedPartial,
  LabelNumericValue,
  NavelStatus,
  NewCalfEntryInputExtended,
  PenPartial,
  StringMapPartial,
} from './types';
import { useCalfEntryContext } from './useCalfEntryContext';

type CalfEntryFormProps = {
  className?: string;
  pens?: PenPartial[];
  breeds?: BreedPartial[];
};

export const CalfEntryForm = ({
  className,
  pens,
  breeds,
}: CalfEntryFormProps) => {
  const { calves, setCalves } = useCalfEntryContext();

  const [_calfEntry, setCalfEntry] =
    useState<NewCalfEntryInputExtended>(initialCalfState);

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<NewCalfEntryInputExtended>({
    defaultValues: initialCalfState as NewCalfEntryInputExtended,
  });

  const remapStringMap = useCallback(
    (items: StringMapPartial[] | undefined | null) =>
      items?.map((item: StringMapPartial) => ({
        label: item.value,
        value: item.attributeValue,
      })) as LabelNumericValue[] | undefined,
    [],
  );

  const { data: sexData } = useQuery(GET_SEX);
  const sexes = useMemo(
    () =>
      remapStringMap(sexData?.stringMaps?.items as StringMapPartial[] | null),
    [remapStringMap, sexData?.stringMaps?.items],
  );

  const { data: testicularDevelopmentData } = useQuery(
    GET_TESTICULAR_DEVELOPMENT,
  );
  const testicularDevelopment = useMemo(
    () =>
      remapStringMap(
        testicularDevelopmentData?.stringMaps?.items as
          | StringMapPartial[]
          | null,
      ),
    [remapStringMap, testicularDevelopmentData?.stringMaps?.items],
  );

  const { data: timeOfDayData } = useQuery(GET_TIME_OF_DAY);
  const col1FeedingTime = useMemo(
    () =>
      remapStringMap(
        timeOfDayData?.stringMaps?.items as StringMapPartial[] | null,
      ),
    [remapStringMap, timeOfDayData?.stringMaps?.items],
  );
  const col2FeedingTime = useMemo(
    () =>
      remapStringMap(
        timeOfDayData?.stringMaps?.items as StringMapPartial[] | null,
      ),
    [remapStringMap, timeOfDayData?.stringMaps?.items],
  );

  const getFormErrorMessage = (name: string) => {
    return (
      //@ts-expect-error: just need to confirm that errors[name] exists
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const onSubmit: SubmitHandler<NewCalfEntryInputExtended> = (
    data: NewCalfEntryInputExtended,
  ) => {
    const newCalf = {
      ...data,
      pen: data.pen,
      penId: data.penId,
      penName: pens?.find((p) => p.id === data.penId)?.name,
      breedId: data.breedId,
      breedName: breeds?.find((b) => b.id === data.breedId)?.name,
      yardId: pens?.find((p) => p.id === data.penId)?.yard?.id,
    };

    setCalfEntry(newCalf);
    setCalves([...calves, newCalf]);
    reset();
  };

  const handleResetForm = () => {
    reset();
    clearErrors();
  };

  const endContent = () => (
    <>
      <Button
        className="calf-entry-form__reset"
        label="Reset"
        icon="pi pi-trash"
        severity="danger"
        onClick={handleResetForm}
      />
      <Button
        className="calf-entry-form__add"
        type="submit"
        label="Add Calf"
        icon="pi pi-plus"
        severity="secondary"
      />
    </>
  );

  return (
    <form
      className={`calf-entry-form ${className}`.trim()}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Toolbar
        className="calf-entry-form__actions toolbar-above-panel"
        end={endContent}
      />

      <div className="calf-entry-form-container content-panel">
        <div className="form-field form-dd-entry-date">
          <label>DD Entry Date</label>
          <Controller
            name="ddEntryDate"
            control={control}
            rules={{ required: '"DD Entry Date" is required.' }}
            render={({ field, fieldState }) => (
              <Calendar
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value ? new Date(field.value) : null}
                showIcon
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('ddEntryDate')}
          </small>
        </div>

        <div className="form-field form-barn-pen">
          <label>Barn/Pen</label>
          <Controller
            name="penId"
            control={control}
            rules={{ required: '"Barn/Pen" is required.' }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                options={pens}
                optionLabel="name"
                optionValue="id"
                virtualScrollerOptions={{ itemSize: 25 }}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">{getFormErrorMessage('pen')}</small>
        </div>

        <div className="form-field form-calf-tag">
          <label>Calf ID</label>
          <Controller
            name="tag"
            control={control}
            rules={{ required: '"Calf ID" is required.' }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <small className="input-error">{getFormErrorMessage('tag')}</small>
        </div>

        <div className="form-field form-birth-date">
          <label>Birth Date</label>
          <Controller
            name="birth"
            control={control}
            rules={{ required: '"Birthdate" is required.' }}
            render={({ field, fieldState }) => (
              <Calendar
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value ? new Date(field.value) : null}
                dateFormat="mm-dd-yy"
                mask="99/99/9999"
                showIcon
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">{getFormErrorMessage('birth')}</small>
        </div>

        <div className="form-field form-rfid">
          <label>RFID</label>
          <Controller
            name="registry"
            control={control}
            rules={{ required: '"RFID" is required.' }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('registry')}
          </small>
        </div>

        <div className="form-field form-breed">
          <label>Breed</label>
          <Controller
            name="breedId"
            control={control}
            rules={{ required: '"Breed" is required.' }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                options={breeds}
                optionLabel="name"
                optionValue="id"
                virtualScrollerOptions={{ itemSize: 25 }}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">{getFormErrorMessage('breed')}</small>
        </div>

        <div className="form-field form-sex">
          <label>Sex</label>
          <Controller
            name="sex"
            control={control}
            rules={{ required: '"Sex" is required.', min: 1 }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                options={sexes}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">{getFormErrorMessage('sex')}</small>
        </div>

        <div className="form-field form-weight">
          <label>Weight</label>
          <Controller
            name="weight"
            control={control}
            rules={{ required: '"Weight" is required.', min: 1 }}
            render={({ field, fieldState }) => (
              <InputNumber
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">{getFormErrorMessage('weight')}</small>
        </div>

        <div className="form-field form-temp">
          <label>Temperature</label>
          <Controller
            name="temp"
            control={control}
            rules={{ required: '"Temperature" is required.', min: 1 }}
            render={({ field, fieldState }) => (
              <InputNumber
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">{getFormErrorMessage('temp')}</small>
        </div>

        <div className="form-field form-testicular-development">
          <label>Testicular Development</label>
          <Controller
            name="testicularDevelopment"
            control={control}
            rules={{
              required: '"Testicular Development" is required.',
              min: 1,
            }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                options={testicularDevelopment}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('testicularDevelopment')}
          </small>
        </div>

        <div className="form-field form-testicular-date">
          <label>Testicular Date</label>
          <Controller
            name="testiclesDecendedOn"
            control={control}
            rules={{ required: '"Testicular Date" is required.' }}
            render={({ field, fieldState }) => (
              <Calendar
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value ? new Date(field.value) : null}
                dateFormat="mm-dd-yy"
                mask="99/99/9999"
                showIcon
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('testiclesDecendedOn')}
          </small>
        </div>

        <div className="form-field form-col-1-date">
          <label>Col. 1 Date</label>
          <Controller
            name="col1FeedingDate"
            control={control}
            rules={{
              required: '"Col. 1 Date" is required.',
              deps: ['col1FeedingTime', 'col1FeedingAmount'],
            }}
            render={({ field, fieldState }) => (
              <Calendar
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value ? new Date(field.value) : null}
                dateFormat="mm-dd-yy"
                mask="99/99/9999"
                showIcon
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('col1FeedingDate')}
          </small>
        </div>

        <div className="form-field form-col-1-time">
          <label>Col. 1 Time</label>
          <Controller
            name="col1FeedingTime"
            control={control}
            rules={{
              min: 1,
            }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                options={col1FeedingTime}
                virtualScrollerOptions={{ itemSize: 25 }}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('col1FeedingTime')}
          </small>
        </div>

        <div className="form-field form-col-1-amount">
          <label>Col. 1 Amount</label>
          <Controller
            name="col1FeedingAmount"
            control={control}
            rules={{
              required: '"Col. 1 Feeding Amount" is required.',
              min: 1,
              deps: ['col1FeedingDate', 'col1FeedingTime'],
            }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('col1FeedingAmount')}
          </small>
        </div>

        <div className="form-field form-col-2-date">
          <label>Col. 2 Date</label>
          <Controller
            name="col2FeedingDate"
            control={control}
            rules={{
              deps: ['col1FeedingTime', 'col1FeedingAmount'],
            }}
            render={({ field, fieldState }) => (
              <Calendar
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value ? new Date(field.value) : null}
                dateFormat="mm-dd-yy"
                mask="99/99/9999"
                showIcon
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('col2FeedingDate')}
          </small>
        </div>

        <div className="form-field form-col-2-time">
          <label>Col. 2 Time</label>
          <Controller
            name="col2FeedingTime"
            control={control}
            rules={{
              deps: ['col1FeedingDate', 'col1FeedingAmount'],
            }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                options={col2FeedingTime}
                virtualScrollerOptions={{ itemSize: 25 }}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('col2FeedingTime')}
          </small>
        </div>

        <div className="form-field form-col-2-amount">
          <label>Col. 2 Amount</label>
          <Controller
            name="col2FeedingAmount"
            control={control}
            rules={{
              deps: ['col1FeedingDate', 'col1FeedingTime'],
            }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('col2FeedingAmount')}
          </small>
        </div>

        <div className="form-field form-navel-status">
          <label>Navel Status</label>
          <Controller
            name="navelStatus"
            control={control}
            rules={{ required: '"Navel Status" is required.' }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                className={classNames({ 'p-invalid': fieldState.invalid })}
                value={field.value}
                options={Object.values(NavelStatus)}
                virtualScrollerOptions={{ itemSize: 25 }}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('navelStatus')}
          </small>
        </div>

        <div className="form-field form-comments">
          <label>Comments</label>
          <Controller
            name="comments"
            control={control}
            render={({ field }) => (
              <InputTextarea
                id={field.name}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <small className="input-error">
            {getFormErrorMessage('comments')}
          </small>
        </div>
      </div>
    </form>
  );
};
